import { Component, OnInit } from '@angular/core';
import { Strings } from '../../../../common/language/strings';
import { res } from '../../../../common/language/text/res';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UserCredential } from '../../../../common/model/UserCredential';
import { UserCredentialDto } from '../../../../common/model/dto/UserCredentialDto';
import { SecurityFacade } from '../../../../common/facade/SecurityFacade';
import { Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from '../../../../common/facade/confirmation.service';
import { RegExpValidation } from '../../../../common/util/RegExpValidation';

@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.css']
})
export class UserModalComponent implements OnInit {
  // Labels and text
  public modalTitle: res;
  public modalClose: res;
  public modalSave: res;
  public modalUpdate: res;
  public modaLoading: res;
  
  public accountLabel: res;
  public nameLabel: res;
  public emailLabel: res;
  public phoneLabel: res;
  public activeDirectoryLabel: res;
  public domainLabel: res;
  public descriptionLabel: res;
  public passwordLabel: res;
  public apiUserLabel: res;
  public customUserLabel: res;
  public positionLabel: res;
  public statusLabel: res;
  public selectAnOption: res;
  public emailNotValid: res;

  public generatePasswordLabel: res;

  public userAlreadyExists: res;
  public userEditError: res;

  public user: UserCredentialDto;
  public onClose$: Subject<number>;
  public _isEdit: boolean;

  public showPassword: boolean;

  private _requiredFields: string[] = [
    'account',
    'password',
    'name',
    'email',
    'domain',
    'description'
  ];

  public statuses: { label: res, value: number }[];
  public domains: string[];
  public descriptions: string[];

  public alerts: { msg: string, type: string }[];

  public _loading: boolean;

  public regexpValidation = RegExpValidation;

  constructor(
    public strings: Strings,
    public bsModalRef: BsModalRef,
    private securityService: SecurityFacade,
    private confirmationService: ConfirmationService
  ) { 
    this.modalTitle = res.User;
    this.modalClose = res.Close;
    this.modalSave = res.Save;
    this.modalUpdate = res.Update;
    this.modaLoading = res.Loading;

    this.accountLabel = res.Account;
    this.nameLabel = res.Name;
    this.emailLabel = res.Email;
    this.phoneLabel = res.Phone;
    this.activeDirectoryLabel = res.ActiveDirectory;
    this.domainLabel = res.Domain;
    this.descriptionLabel = res.Description;
    this.passwordLabel = res.Password;
    this.apiUserLabel = res.ApiUser;
    this.customUserLabel = res.CustomUser;
    this.positionLabel = res.Position;
    this.statusLabel = res.Status;
    this.selectAnOption = res.SelectAnOption;
    this.emailNotValid = res.InvalidEmail;

    this.generatePasswordLabel = res.GeneratePassword;

    this.userAlreadyExists = res.UserAlreadyExists;
    this.userEditError = res.UserEditError;

    this.user = new UserCredentialDto();
    this.onClose$ = new Subject();
    this._isEdit = false;

    this.showPassword = false;

    this.statuses = [
      { label: res.Inactive, value: 0 },
      { label: res.Active, value: 1 }
    ];
    this.domains = [
      'charly.com',
      'charly.com.mx'
    ];
    this.descriptions = [
      'Usuario Quantum',
      'Tienda Quantum',
    ];

    this.alerts = [];

    this._loading = false;
  }

  ngOnInit(): void {
    if (this.user.isActiveDirectory){
      this._requiredFields = this._requiredFields.filter(f => f !== 'password');
    }

    if (this.user.description != null) {
      this.descriptions.forEach(d => {
        if (d.toUpperCase() === this.user.description.toUpperCase().trim())
          this.user.description = d;
      });
    }
  }

  public closeModal(): void {
    this.onClose$.next(0);
    this.bsModalRef.hide();
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public async saveUser(): Promise<void> {
    const result: boolean = await this.confirmationService.confirm();
    if (!result)
      return;

    for (const key in this.user) {
      if (this.user.hasOwnProperty(key) && typeof this.user[key] === 'string') {
        this.user[key] = this.user[key].trim();
      }
    }

    this._loading = true;
    this.securityService.AddUserCredential(this.user)
    .pipe(finalize(() => this._loading = false))
    .subscribe(user => {
      this.onClose$.next(1);
      this.bsModalRef.hide();

    }, error => {
      this.addAlert(`<strong> ${this.strings.GetText(this.userAlreadyExists)} </strong>`, 'danger');
    });
  }

  public async editUser(): Promise<void> {
    const result: boolean = await this.confirmationService.confirm();
    if (!result)
      return;
    
    for (const key in this.user) {
      if (this.user.hasOwnProperty(key) && typeof this.user[key] === 'string') {
        this.user[key] = this.user[key].trim();
      }
    }

    this._loading = true;
    this.securityService.UpdateUserCredential(this.user)
    .pipe(finalize(() => this._loading = false))
    .subscribe(user => {
      this.onClose$.next(1);
      this.bsModalRef.hide();

    }, error => {
      this.addAlert(`<strong> ${this.strings.GetText(this.userEditError)} </strong>`, 'danger');
    });
  }

  public isFormValid(): boolean {
    for (const field of this._requiredFields) {
      const value = this.user[field as keyof UserCredentialDto];
      
      if (value === null || value === undefined || value === '' || value.toString().trim() === '')
        return false;
    }

    return true;
  }

  public addAlert(message: string, type: string): void {
    this.alerts = [];
    this.alerts.push({
      type: type,
      msg: message
    });
  }

  public onAlertClose(dismissedAlert: { msg: string, type: string }): void {
    this.alerts = this.alerts.filter(alert => alert !== dismissedAlert);
  }

  public onActiveDirectoryChange(event): void {
    if (event.target.checked) {
      this._requiredFields = this._requiredFields.filter(f => f !== 'password');
    } else {
      this._requiredFields.push('password');
    }
  }

  public isPasswordRequired(): boolean {
    return this._requiredFields.includes('password');
  }

  public preventRestrictedCharacters(event: KeyboardEvent, regex: RegExp): void {
    const char = event.key;

    if (!regex.test(char)) {
      event.preventDefault();
    }
  }

  public isEmailValid(): boolean {
    return this.regexpValidation.accountEmail(this.user.account).test(this.user.email);
  }

  public isFieldValid(field: string): boolean {
    return this.regexpValidation[field](this.user[field]).test(this.user[field]);
  }

  public generatePassword(): void {
    this.user.password = this.generateRandomPassword();
  }

  private generateRandomPassword(length: number = 8): string {
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const uppercaseChars = lowercaseChars.toUpperCase();
    const numberChars = '0123456789';
    const symbolChars = '!@#$%^&*()_+-=[]{};:,.<>?';
  
    const allChars = uppercaseChars + lowercaseChars + numberChars + symbolChars;
  
    const password = [
      uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)],
      lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)],
      numberChars[Math.floor(Math.random() * numberChars.length)],
      symbolChars[Math.floor(Math.random() * symbolChars.length)]
    ];
  
    for (let i = 4; i < length; i++) {
      password.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }
  
    return password
      .sort(() => Math.random() - 0.5)
      .join('');
  }

}
